import React from "react";
import _ from "lodash";
import numeral from "numeral";
import moment from "moment-timezone";
import ModalWrapper from "components/common/ModalWrapper";

import ClickToCopyWrapper from "components/common/ClickToCopyWrapper";
import LoadingUI from "components/common/LoadingUI";
import UprPriceSection from "components/network/pricing-rules/gam-upr/UprPriceSection";
import HighLightRowWrapper from "components/common/HighLightRowWrapper";
import UprTargetingPopover from "components/ops-mgmt/account/custom-upr/UprTargetingPopover";
import HighchartWrapper from "components/common/HighchartWrapper";
import PPLabel from "components/common/PPLabel";
import LegendControl from "components/common/LegendControl";
import SwitchTabs from "components/common/SwitchTabs";
import {
  getPublisherUprPieChartOptions,
  getPublisherUprImpGeneralChartOptions,
  CHART_TYPES,
} from "components/anatomy-cst/AnatomyCstChartHelper";
import { NetworkAPI, UnitAPI } from "apis";
import { tdClass, thClass } from "helpers/StyleClass";
import { transformGamUprToUprConfig } from "components/network/pricing-rules/gam-upr/GamUprHelper";
import { setQueryString } from "helpers/QueryString";
import { getDateList } from "helpers/DateTransform";

import { FiFileText } from "react-icons/fi";

class PublisherUprModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      errMsg: null,
      isLoading: false,
      popupId: "",

      uprData: null,
      selectedItems: [],
      dateList: [],

      pieChartOptions: null,
      impChartOptions: null,
      perChartOptions: null,
      legendList: null,

      networkInfo: null,
      units: null,
      virtualPlacements: null,

      chartOptions: [
        {
          label: "Impression",
          value: CHART_TYPES.IMPRESSION,
        },
        {
          label: "Percentage",
          value: CHART_TYPES.PERCENTAGE,
        },
      ],
      selectedChartType: CHART_TYPES.PERCENTAGE,
    };

    this.chartRefs = [React.createRef(), React.createRef()];
    this.pieChartRef = React.createRef();

    this.handlePopup = this.handlePopup.bind(this);
    this.handleSelectItem = this.handleSelectItem.bind(this);
    this.handleImportSelected = this.handleImportSelected.bind(this);
  }

  async componentDidMount() {
    const { unitId, networkId, startDate, includeToday } = this.props;
    try {
      this.setState({ isLoading: true });

      if (unitId && startDate) {
        const { reports, props } = await UnitAPI.getBenchmarkUprReport({
          unitId,
          startDate: startDate.split(" ")[0],
        });

        const { upr = {} } = await NetworkAPI.getNetworkResourceMapping({
          networkId,
        });

        const { config } = await NetworkAPI.getRawNetworkFeatures({
          networkId,
        });

        const protectedUprIds = _.get(config, "publisherUPRProtectionList", []);
        const preservedUprIds = _.get(config, "preservedUPRIds", []);

        const resourceById = _.keyBy(upr.mapping, (item) => {
          return item.id;
        });

        const dateList = getDateList(
          startDate.split(" ")[0],
          moment().format("YYYY-MM-DD"),
          includeToday
        );

        // [uprid]: number[] (impression by date)
        const uprImpression = {};

        _.forEach(reports, (item) => {
          const { date, external_id, impression } = item;
          const dIdx = _.indexOf(dateList, date);
          if (uprImpression[external_id]) {
            uprImpression[external_id][dIdx] = impression;
          } else {
            uprImpression[external_id] = new Array(dateList.length).fill(0);
            uprImpression[external_id][dIdx] = impression;
          }
        });

        const amountByDate = _.values(uprImpression).reduce((acc, cur) => {
          return acc.map((value, index) => value + cur[index]);
        }, new Array(dateList.length).fill(0));

        const amount = _.sum(amountByDate);

        // [uprid]: number[] (percentage by date)
        const uprAverageImpression = _.mapValues(uprImpression, (list) => {
          return _.map(list, (d, i) => parsePercentage(d, amountByDate[i]));
        });

        const uprAmount = _.orderBy(
          _.map(_.keys(uprImpression), (id) => {
            return {
              name: props[id] ? props[id].uprName : "",
              value: _.sum(uprImpression[id]),
            };
          }),
          "value",
          "desc"
        );

        const slicedUpr =
          uprAmount.length > 10
            ? [
                ..._.slice(uprAmount, 0, 9),
                {
                  name: "Others",
                  value: _.sum(_.slice(uprAmount, 9).map((d) => d.value)),
                },
              ]
            : uprAmount;

        const otherList = _.slice(uprAmount, 9)
          .map((d) => d.name)
          .map((name) => {
            return _.findKey(props, (item) => item.uprName === name);
          });

        const impressionData = _.map(slicedUpr, ({ name }) => {
          if (name !== "Others") {
            const id = _.findKey(props, (item) => item.uprName === name);
            return {
              name,
              data: uprImpression[id],
            };
          } else {
            return {
              name,
              data: _.reduce(
                otherList,
                (acc, id) => {
                  return acc.map(
                    (value, index) => value + uprImpression[id][index]
                  );
                },
                new Array(dateList.length).fill(0)
              ),
            };
          }
        });

        const percentageData = _.map(slicedUpr, ({ name }) => {
          if (name !== "Others") {
            const id = _.findKey(props, (item) => item.uprName === name);
            return {
              name,
              data: uprAverageImpression[id],
            };
          } else {
            return {
              name,
              data: _.reduce(
                otherList,
                (acc, id) => {
                  return acc.map(
                    (value, index) => value + uprAverageImpression[id][index]
                  );
                },
                new Array(dateList.length).fill(0)
              ).map((d) => _.toNumber(d.toFixed(2))),
            };
          }
        });

        this.setState({
          uprData: _.orderBy(
            _.map(_.values(props), (upr) => {
              const newUpr = transformGamUprToUprConfig({ gamUpr: upr });

              newUpr.__isI2wRule = _.startsWith(upr.uprName, "Intowow_");
              newUpr.__isPubRule = !_.startsWith(upr.uprName, "Intowow_");
              newUpr.__isProtected =
                _.indexOf(protectedUprIds, _.parseInt(upr.externalId)) !== -1;
              newUpr.__isPreserved =
                _.indexOf(preservedUprIds, _.parseInt(upr.externalId)) !== -1;
              return {
                ...newUpr,
                props: _.get(resourceById, [upr.externalId, "props"], null),
                impression: _.sum(uprImpression[upr.externalId] || []),
                ratio: parsePercentage(
                  _.sum(uprImpression[upr.externalId]),
                  amount,
                  2
                ),
              };
            }),
            "impression",
            "desc"
          ),
          pieChartOptions: getPublisherUprPieChartOptions(slicedUpr),
          impChartOptions: getPublisherUprImpGeneralChartOptions({
            xAxis: dateList,
            impression: impressionData,
            percentage: percentageData,
            chartType: CHART_TYPES.IMPRESSION,
          }),
          perChartOptions: getPublisherUprImpGeneralChartOptions({
            xAxis: dateList,
            impression: impressionData,
            percentage: percentageData,
            chartType: CHART_TYPES.PERCENTAGE,
          }),
          dateList,
          legendList: _.map(slicedUpr, (item) => item.name),
        });
      }

      if (networkId) {
        const networkInfo = await NetworkAPI.getNetworkInfo({
          networkId,
        });
        const units = await NetworkAPI.getUnitsByNetworkId({ networkId });
        const virtualPlacements = await NetworkAPI.getNetworkVirtualPlacements({
          networkId,
        });

        this.setState({
          networkInfo,
          units,
          virtualPlacements,
        });
      }
    } catch (error) {
      this.setState({
        errMsg: error.message,
      });
    }

    this.setState({ isLoading: false });
  }

  handlePopup(externalId) {
    this.setState({ popupId: externalId });
  }

  handleSelectItem(id) {
    const { selectedItems } = this.state;

    if (id === "ALL") {
      if (selectedItems.length !== this.state.uprData.length) {
        this.setState({
          selectedItems: _.map(this.state.uprData, (item) => item.externalId),
        });
      } else {
        this.setState({ selectedItems: [] });
      }
    } else {
      const index = _.indexOf(selectedItems, id);
      if (index === -1) {
        this.setState({
          selectedItems: [...selectedItems, id],
        });
      } else {
        this.setState({
          selectedItems: _.filter(selectedItems, (item) => item !== id),
        });
      }
    }
  }

  handleImportSelected() {
    const { selectedItems, networkInfo } = this.state;
    const queryString = setQueryString(
      this.props,
      { importId: selectedItems },
      true
    );
    window.open(
      `${window.location.origin}/#/ops-mgmt/account/network/${networkInfo.networkId}/upr-setting?${queryString}`
    );
  }

  render() {
    const { handleClose } = this.props;

    const {
      uprData,
      selectedItems,
      networkInfo,
      units,
      dateList,
      virtualPlacements,
      popupId,
      pieChartOptions,
      impChartOptions,
      perChartOptions,
      legendList,
      chartOptions,
      selectedChartType,
      isLoading,
      errMsg,
    } = this.state;

    const parseChartOptions =
      selectedChartType === CHART_TYPES.IMPRESSION
        ? impChartOptions
        : perChartOptions;

    return (
      <ModalWrapper
        isOpen={true}
        handleClose={handleClose}
        showCloseFooter={true}
        customOverlayStyle={{ zIndex: 100 }}
        width="70%"
        title="Benchmark Publisher UPR"
      >
        {isLoading && (
          <div className="flex justify-center">
            <LoadingUI iconOnly={true}></LoadingUI>
          </div>
        )}
        {errMsg && (
          <div className="flex justify-center">
            <div className="text-red-600">{errMsg}</div>
          </div>
        )}

        {uprData && networkInfo && (
          <>
            <div className="mb-2 flex items-end justify-between">
              <div>
                Date Range:{" "}
                <span className="font-semibold text-blue-600">
                  {dateList[0]} ~
                </span>
              </div>
              <button
                type="button"
                onClick={this.handleImportSelected}
                className={`rounded px-6 py-2 font-semibold text-white ${
                  selectedItems.length === 0
                    ? "bg-blue-200"
                    : "shadow bg-blue-400 hover:bg-blue-500"
                }`}
                disabled={selectedItems.length === 0}
              >
                Import selected
              </button>
            </div>
            <div
              className="mb-4 overflow-y-auto"
              style={{ maxHeight: "320px" }}
            >
              <table className="table w-full table-auto text-sm">
                <thead className="bg-gray-200 text-xs">
                  <tr>
                    <th className={thClass + " w-12 text-center"}>
                      <input
                        style={{
                          width: "18px",
                          height: "18px",
                          marginTop: "4px",
                        }}
                        type="checkbox"
                        checked={
                          uprData.length > 0 &&
                          selectedItems.length === uprData.length
                        }
                        onChange={() => this.handleSelectItem("ALL")}
                      ></input>
                    </th>
                    <th className={thClass + " w-40"}>External ID</th>
                    <th className={thClass} colSpan={1}>
                      Name
                    </th>
                    <th className={thClass}></th>
                    <th className={thClass + " w-40 text-right"}>
                      Price Section
                    </th>
                    <th className={thClass + " w-32 text-right"}>Impression</th>
                    <th className={thClass + " w-12"}></th>
                  </tr>
                </thead>
                <tbody>
                  {uprData.map((item, index) => {
                    const gamLink = `https://admanager.google.com/${
                      networkInfo ? networkInfo.code : ""
                    }#inventory/pricing/detail/pricing_id=${
                      item.externalId
                    }&tab=settings`;

                    return (
                      <HighLightRowWrapper
                        selected={popupId === item.externalId}
                        key={index}
                      >
                        <td className={tdClass + " text-center"}>
                          <input
                            style={{
                              width: "18px",
                              height: "18px",
                              marginTop: "4px",
                            }}
                            type="checkbox"
                            checked={
                              _.indexOf(selectedItems, item.externalId) !== -1
                            }
                            onChange={() =>
                              this.handleSelectItem(item.externalId)
                            }
                          ></input>
                        </td>
                        <td className="border py-2 pl-4 pr-1">
                          <div className="flex items-center justify-between">
                            <span
                              className={`font-mono ${
                                popupId === item.externalId
                                  ? "font-semibold"
                                  : ""
                              }`}
                            >
                              {item.externalId}
                            </span>

                            <div className="flex items-center text-blue-600">
                              <ClickToCopyWrapper
                                copyText={gamLink}
                                tooltipMessage="Click to copy GAM link"
                              ></ClickToCopyWrapper>
                            </div>
                          </div>
                        </td>
                        <td className={tdClass + " border-r-0"}>
                          <div
                            className={`flex items-center ${
                              popupId === item.externalId ? "font-semibold" : ""
                            }`}
                          >
                            {item.uprName}
                          </div>
                        </td>
                        <td className={tdClass + " border-l-0"}>
                          <div
                            className={`flex items-center justify-center ${
                              popupId === item.externalId ? "font-semibold" : ""
                            }`}
                          >
                            <PPLabel item={item} isSmall={true}></PPLabel>
                          </div>
                        </td>
                        <td className="border px-2 py-1 text-right">
                          <UprPriceSection
                            item={item.priceSection}
                            currency={item.priceSection.currency}
                            networkId={networkInfo.networkId}
                            onOpen={() => this.handlePopup(item.externalId)}
                            onClose={() => this.handlePopup("")}
                          ></UprPriceSection>
                        </td>
                        <td className={tdClass + " text-right font-mono"}>
                          <span>{item.ratio}%</span>
                          <div className="text-xs text-gray-600">
                            {numeral(item.impression).format("0,0")}
                          </div>
                        </td>
                        <td className={tdClass + " text-center"}>
                          <div>
                            {item.targetingSection && (
                              <UprTargetingPopover
                                id={item.externalId}
                                name={item.uprName}
                                place="left"
                                networkId={networkInfo.networkId}
                                units={units}
                                virtualPlacements={virtualPlacements}
                                targetingSection={item.targetingSection}
                                triggerElement={
                                  <div className="cursor-pointer  text-blue-600 hover:text-blue-700">
                                    <FiFileText />
                                  </div>
                                }
                                onOpen={() => this.handlePopup(item.externalId)}
                                onClose={() => this.handlePopup("")}
                              ></UprTargetingPopover>
                            )}
                          </div>
                        </td>
                      </HighLightRowWrapper>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="mb-4 flex w-full gap-4" style={{ height: "360px" }}>
          <div className="flex w-2/3 flex-col justify-end gap-2">
            {parseChartOptions && (
              <>
                <div className="flex justify-end">
                  <SwitchTabs
                    tabs={chartOptions}
                    selectedTabKey={selectedChartType}
                    handleChange={(value) => {
                      this.setState({ selectedChartType: value });
                    }}
                  ></SwitchTabs>
                </div>
                <HighchartWrapper
                  ref={this.chartRefs[0]}
                  options={parseChartOptions}
                  key={selectedChartType}
                ></HighchartWrapper>
              </>
            )}
          </div>
          <div className="w-1/3">
            {pieChartOptions && (
              <HighchartWrapper
                ref={this.pieChartRef}
                options={pieChartOptions}
              ></HighchartWrapper>
            )}
            {legendList && legendList.length > 0 && (
              <div
                className="border rounded overflow-y-auto border-blue-200 bg-gray-100 px-4 py-2"
                style={{ maxHeight: "160px" }}
              >
                <LegendControl
                  data={legendList}
                  chartRefs={[...this.chartRefs, this.pieChartRef]}
                ></LegendControl>
              </div>
            )}
          </div>
        </div>
      </ModalWrapper>
    );
  }
}

function parsePercentage(value, total, decimal = 1) {
  return total > 0 ? _.toNumber(((value / total) * 100).toFixed(decimal)) : 0;
}

export default PublisherUprModal;
